import type { Community } from './Community.ts'
import type { optionType } from './dictionary.ts'
import type { EntityType } from './EntityType.ts'
import type { UserFavorites } from './Favorites.ts'
import type { FeedbackAction, FeedbackId } from './Feedback.ts'
import type { DocumentWithNullableTimestamps, DocumentWithTimestamps, Timestamp } from './firebase.ts'
import type { OrgId, RequestLimit, UserId, UserRegisteredEmail } from './ids.ts'
import type { SharedAssessment } from './JobRequest.ts'
import type { FormOfCollaborationType, JobPlaceType, JobTimeType, JobType } from './Job.ts'
import type { Language } from './Language.ts'
import type { MentoringCategoryItem } from './Mentoring.ts'
import type { MyProjects } from './MyProjects.ts'
import type { ConsentsData } from './Org.ts'
import type { RelatedItems } from './RelatedItems.ts'
import type { VisibilityType } from './visibility.ts'

export interface FeedbackMeta {
    id: FeedbackId
    action: FeedbackAction
    createdAt: Timestamp
    requestId?: string
    /** Present when the feedback carried an NPS score - used to suppress repeat NPS asks. */
    nps?: number
}

export enum UserRoles {
    Admin = 'admin',
    SelfKnowledgeAdmin = 'self-knowledge-admin',
    /**
     * Moderátor / koordinátor Krúžkov. Vidí zoznam prihlášok a tipov v
     * /admin/kruzky, ale nie zvyšok administrácie.
     *
     * ! Vo fáze 1 ešte neexistuje krúžok ako entita, takže rola je globálna —
     * moderátor vidí všetky prihlášky a tipy, nie „svoju skupinu". Zúženie na
     * konkrétny krúžok príde vo fáze 2 spolu s členstvami.
     */
    KruzkyModerator = 'kruzky-moderator'
}

export interface ExternalConsentResponse {
    valid: boolean
    validUntil?: string | number
    lastChange?: number
}

export type UserContactProps = Partial<
    Pick<UserProfile & UserPrivateProfile, 'firstName' | 'lastName' | 'contactEmail' | 'phone'>
>

export const responseUnits: optionType[] = [
    {
        id: 'hours',
        value: 'hodín',
        label: 'hodín'
    },
    { id: 'days', value: 'dní', label: 'dní' },
    {
        id: 'weeks',
        value: 'týždňov',
        label: 'týždňov'
    }
]

export type Experience = {
    value?: boolean
    where?: string[]
    more?: string
}

export type UserProfileType =
    | EntityType.Mentor
    | EntityType.Volunteer
    | EntityType.UserProfile
    | EntityType.TalentBaseProfile
    | EntityType.UserPrivateProfile

export enum VolunteerPrefersFieldOfExperience {
    YES = 'yes',
    NO = 'no',
    EITHER = 'either'
}

export interface MentorQuickReply {
    enabled: boolean
    message: string
}

export type MentorProfile = DocumentWithNullableTimestamps & {
    who?: string[]
    how?: MentoringCategoryItem[]
    academicalArea?: MentoringCategoryItem[]
    expertise?: MentoringCategoryItem[]
    interests?: string[]
    motivation?: string // "Prečo sa chceš stať mentorom/kou?"
    relatedItems?: RelatedItems
    visibility: VisibilityType
    trainingCompletedAt?: Timestamp | null
    experience?: Experience
    /** @deprecated "Neprijímam žiadosti" was removed (2026-10). Availability is visibility only; cleared by scripts/migrations/remove-mentoring-requests-pause.cjs. */
    requestsPausedAt?: Timestamp | null
    topAutoImported?: boolean // Flag: TOP application data was auto-imported into this profile
    quickReply?: MentorQuickReply
}

export interface UserName {
    firstName?: string
    lastName?: string
}

export interface Credentials {
    email: string
    password: string
}

export interface JobPosition {
    position?: string
    org?: string
    address?: string
    desc?: string
    startYear?: number
    endYear?: number
}

export function createDefaultJobPosition(): JobPosition {
    return {
        position: '',
        org: '',
        address: '',
        desc: '',
        startYear: new Date().getFullYear()
    }
}

export interface Achievement {
    achievement: string
    org?: string
    year?: number
    desc?: string
}

export const defaultAchievement = {
    achievement: '',
    org: '',
    year: 2020,
    desc: ''
}

export interface School {
    school?: string
    address?: string
    degree?: string
    field?: string // Updated from level/specialistation to field
    startDate?: Timestamp | Date | string
    endDate?: Timestamp | Date | string
    description?: string // Updated from desc to description
    // Legacy fields for backward compatibility
    level?: string
    startYear?: number
    endYear?: number
    desc?: string
    specialistation?: string
}

export const defaultSchool = {
    school: '',
    address: '',
    degree: '',
    field: '',
    startDate: undefined,
    endDate: undefined,
    description: '',
    // Legacy defaults for backward compatibility
    level: '',
    startYear: 2020,
    endYear: 2020,
    desc: '',
    specialistation: ''
}

export interface UserSocialNetwork {
    network: string
    profileUrl: string
}

export type VolunteerProfile = DocumentWithNullableTimestamps & {
    interests: string[]
    intensityValue: string
    intensityUnit: string
    helpHow: string
    helpWho: string[]
    timeCapacity: string[]
    cooperation: string[]
    prefersFieldOfExperience?: VolunteerPrefersFieldOfExperience
    fieldOfExperienceDetails?: string
    relatedItems?: RelatedItems
    visibility: VisibilityType
    volunteerGeoRegion: string[]
    requestsPausedAt?: Timestamp | null
}

export interface LocationData {
    name: string
    country: string
    lat: number
    lng: number
}

export interface UserLocations {
    location: LocationData
    favoriteLocation: LocationData
}

export type CommunityUserProfile = DocumentWithNullableTimestamps & {
    communityId: Community['id']
    location?: LocationData
    favoriteLocation?: LocationData
    visibility: VisibilityType.published | VisibilityType.hidden
    joinedAt?: Timestamp
    status?: 'participant' | 'graduate'
    groupId?: string
    customFields?: Record<string, unknown>
}

export enum TalentBaseUserProfileStatusType {
    activelySeeking = 'aktivne_hladam',
    offerDependent = 'zalezi_od_ponuky',
    notSeeking = 'nehladam'
}

export type TalentBaseUserProfile = DocumentWithNullableTimestamps & {
    slug: string
    why: string
    keywords: string[]
    jobPreferences: {
        jobType: Exclude<JobType, 'paidInternship' | 'unpaidInternship'>[]
        formOfCollaboration: FormOfCollaborationType[]
        jobPlace: JobPlaceType[]
        jobLocation?: string[]
        jobTime: JobTimeType[]
        moreAboutFlexibility?: string
    }
    moreAboutMe: {
        future?: string
        succeed?: string
        believe?: string
        failure?: string
        end?: string
    }
    linkedInUrl?: string
    cvUrl: string
    clUrl?: string[]
    status: TalentBaseUserProfileStatusType
    // Self-knowledge results the candidate chose to show companies, one snapshot per test.
    // Visible in the anonymous profile too. Never returned next to the person's name
    // (TALENT_BASE_PRIVATE_FIELDS in server/utils/publicProfile.ts).
    sharedAssessments?: SharedAssessment[]
    /** @deprecated Never decided anything. `status` is the only switch, see isTalentBaseProfileListed. */
    visibility?: VisibilityType
}

export interface MenteeProfile {
    mentee: boolean
    // Training
    trainingCompletedAt?: Timestamp | null
    // Profile status
    visibility?: VisibilityType
    createdAt?: Timestamp | null
    updatedAt?: Timestamp | null
    /** @deprecated Never had any effect for mentees; cleared by scripts/migrations/remove-mentoring-requests-pause.cjs. */
    requestsPausedAt?: Timestamp | null
    // What I'm looking for
    lookingFor?: string[] // Areas where seeking help (e.g., 'career-change', 'leadership', 'work-life-balance')
    currentSituation?: string // Current role, company stage, challenges
    goals?: string // What they want to achieve
    // Preferences
    preferredFormat?: string[] // e.g., 'online', 'in-person', 'both'
    preferredDuration?: string // e.g., 'one-time', 'short-term', 'long-term'
    timeCommitment?: string // e.g., 'monthly', 'bi-weekly', 'weekly'
    topAutoImported?: boolean // Flag: TOP application data was auto-imported into this profile
}

export interface MentorTopProfile extends DocumentWithNullableTimestamps {
    interests: string[] // "S čím viem ako mentor pomôcť" - topics/hashtags
    q1: string // Selection otázka 1
    q2?: string // Selection otázka 2
    q3?: string // Selection otázka 3
    q4?: string // Selection otázka 4
    project?: string // Most significant career project (max 250 words)
    appliedAt?: Timestamp // When form was submitted
    approvedAt?: Timestamp // When admin approved
    approvedBy?: string // Admin user ID who approved
    declinedAt?: Timestamp // When admin declined
    declinedBy?: string // Admin user ID who declined
    offeredMenteeAt?: Timestamp // When admin offered mentee role (too junior for mentor)
    offeredMenteeBy?: string // Admin user ID who offered
    waitingAt?: Timestamp // When admin set to waiting
    waitingById?: string // Admin user ID who set waiting
    revokedAt?: Timestamp // When admin revoked decision
    revokedBy?: string // Admin user ID who revoked
}

export type TopPaymentStatus = 'waiting' | 'paid' | 'overdue'

export interface TopAiFieldGrade {
    field: string
    grade: 'A' | 'B' | 'C' | '0'
}

export interface TopAiDecision {
    decision: 'approve' | 'decline' | 'waiting' | 'offer-mentee'
    confidence: number
    totalScore: number
    maxScore: number
    scoredAt: Timestamp
    summary: string
    fields?: TopAiFieldGrade[]
}

export interface MenteeTopProfile extends DocumentWithNullableTimestamps {
    // NOTE: growthGoal is stored in universal growthGoals field, not here
    workOn?: string // What they want to work on in the program (max 250 words)
    motivation: string // Past project description (max 250 words)
    project: string // Project description (max 250 words)
    commitment: string // Time commitment radio selection
    preferredSegments: string[] // Optional hashtags for preferred mentoring groups
    prioritySegment?: number // Priority rating 1-5 for finding mentor in specific segment
    priorityExperience?: number // Priority rating 1-5 for finding experienced mentor
    priorityNetworking?: number // Priority rating 1-5 for networking importance
    appliedAt?: Timestamp // When form was submitted
    approvedAt?: Timestamp // When admin approved
    approvedBy?: string // Admin user ID who approved
    declinedAt?: Timestamp // When admin declined
    declinedBy?: string // Admin user ID who declined
    waitingAt?: Timestamp // When admin set to waiting
    waitingById?: string // Admin user ID who set waiting
    revokedAt?: Timestamp // When admin revoked decision
    revokedBy?: string // Admin user ID who revoked
}

export interface GrowthGoalTask {
    text: string
    done: boolean
}

export interface GrowthGoal {
    text: string
    progress: number
    tasks: GrowthGoalTask[]
}

export interface Response {
    responsesAverageTime?: number
    responsesAmount?: number
}

export type UserAffiliation = {
    orgId: OrgId
    createdAt: Timestamp
}

export interface UserOnboarding {
    completedAt?: Timestamp
    kodexCompletedAt?: Timestamp
    tipsCompletedAt?: Timestamp
}

export type UserProfile = UserName &
    DocumentWithTimestamps & {
        id: UserId
        slug?: string
        mentorProfile?: MentorProfile
        volunteerProfile?: VolunteerProfile
        communityUserProfiles?: {
            [key: CommunityUserProfile['communityId']]: CommunityUserProfile
        }
        talentBaseUserProfile?: TalentBaseUserProfile
        menteeProfile?: MenteeProfile
        highSchools?: School[]
        universities?: School[]
        jobs?: JobPosition[]
        achievements?: Achievement[]
        socialLinks?: UserSocialNetwork[]
        genders?: string[]
        geoCountry?: string
        geoRegion?: string | null
        geoCity?: string
        livingAbroad?: boolean | null
        profileImg?: string
        shortProfile?: string // Kratky popis
        interests?: string[] // Najdôležitejšie kľúčové slová o mne -> Tagy
        hobby?: string // Viac o mne
        hobbiesAndInterests?: string // Hobby, záujmy
        leafAlumni?: boolean
        leafAlumniWhatAnWhen?: string
        growthGoals?: GrowthGoal[] // Universal growth goals for all users
        feedbackMetas?: FeedbackMeta[]
        responseTime?: number
        responseUnit?: string
        responsesAverageTime?: number
        responsesAmount?: number
        dummy?: boolean
        affiliations?: Record<OrgId, UserAffiliation>
        /**
         * Languages spoken by the user
         */
        languages?: Language[]
        onboarding?: UserOnboarding
        /**
         * Informative field for the admin
         * @description to know if the user has international experience
         */
        hasInternationalExperience?: boolean | null
        /**
         * Informative field for the admin
         * @description to know if the user is a talentbase candidate
         */
        isTalentbaseCandidate?: boolean | null

        // TopMentoring Lifecycle Flags
        mentorTopLead?: boolean // Email captured for mentor application
        menteeTopLead?: boolean // Email captured for mentee application
        mentorTopApplied?: boolean // Mentor application form submitted
        menteeTopApplied?: boolean // Mentee application form submitted
        mentorTopWaiting?: boolean // Admin set mentor application to waiting
        menteeTopWaiting?: boolean // Admin set mentee application to waiting
        mentorTopCommittee?: boolean // Mentor application moved to committee review (1.4)
        menteeTopCommittee?: boolean // Mentee application moved to committee review (1.4)
        mentorTopCallScheduled?: boolean // Mentor has a scheduled selection call (1.5)
        menteeTopCallScheduled?: boolean // Mentee has a scheduled selection call (1.5)
        mentorTopApproved?: boolean // Admin approved mentor application
        menteeTopApproved?: boolean // Admin approved mentee application
        mentorTopOfferMentee?: boolean // Admin offered rejected mentor to become mentee (too junior)
        mentorTopDeclined?: boolean // Admin declined mentor application
        menteeTopDeclined?: boolean // Admin declined mentee application
        mentorTopVip?: boolean // VIP flag on mentor side (priority handling)
        menteeTopVip?: boolean // VIP flag on mentee side (priority handling)
        mentorTopPaymentStatus?: TopPaymentStatus // 4.x payment state for mentor side
        menteeTopPaymentStatus?: TopPaymentStatus // 4.x payment state for mentee side
        mentorBooked?: boolean // Has at least 1 active (Accepted) mentoring
        menteeBooked?: boolean // Has at least 1 active (Accepted) mentoring

        // TopMentoring Profile Data
        mentorTopProfile?: MentorTopProfile
        menteeTopProfile?: MenteeTopProfile

        // TopMentoring AI Decision (overlay, cleared by human action)
        mentorTopAiDecision?: TopAiDecision | null
        menteeTopAiDecision?: TopAiDecision | null

        // TopMentoring Supplement (user adds extra info via link from admin email)
        topMentoringSupplement?: string
        topMentoringSupplementAt?: Timestamp

        // TopMentoring Invite Link (user joined via the topInvite link, not the application form)
        topInviteRedeemed?: boolean
        topInviteRedeemedAt?: Timestamp
        topInviteSource?: string

        // TopMentoring Payment (admin-managed; mentee = required 1% of gross salary, mentor = optional 1–3000 €)
        topMenteePaymentCompleted?: boolean
        topMenteePaymentCompletedAt?: Timestamp
        topMenteePaymentNote?: string
        topMentorPaymentCompleted?: boolean
        topMentorPaymentCompletedAt?: Timestamp
        topMentorPaymentNote?: string

        // TopMentoring Application Data (from application form)
        linkedInUrl?: string // LinkedIn profile URL provided during application
        cvUrl?: string // CV file URL uploaded during application

        updatedById?: string
    }

export type CommunityUserProfileSearchRecord = Partial<CommunityUserProfile> & {
    id: string
    communityId: Community['id']
    visibility: VisibilityType.published | VisibilityType.hidden
    userId: UserProfile['id']
    firstName?: UserProfile['firstName']
    lastName?: UserProfile['lastName']
    name?: string
    profileImg?: UserProfile['profileImg']
    slug?: UserProfile['slug']
    geoCity?: UserProfile['geoCity']
    geoCountry?: UserProfile['geoCountry']
    geoRegion?: UserProfile['geoRegion']
    interests?: UserProfile['interests']
    shortProfile?: UserProfile['shortProfile']
    locationName?: LocationData['name']
    locationCountry?: LocationData['country']
    locationLat?: LocationData['lat']
    locationLng?: LocationData['lng']
    favoriteLocationName?: LocationData['name']
    favoriteLocationCountry?: LocationData['country']
    favoriteLocationLat?: LocationData['lat']
    favoriteLocationLng?: LocationData['lng']
    // Role flags for badges (optional, may not be indexed)
    isMentor?: boolean
    isMentee?: boolean
    isVolunteer?: boolean
    user: Pick<
        UserProfile,
        | 'id'
        | 'firstName'
        | 'lastName'
        | 'profileImg'
        | 'slug'
        | 'geoCity'
        | 'geoCountry'
        | 'geoRegion'
        | 'interests'
        | 'jobs'
        | 'universities'
    >
}

export type SearchTalentBaseUserProfile = Pick<
    UserProfile,
    | 'id'
    | 'firstName'
    | 'lastName'
    | 'profileImg'
    | 'geoCity'
    | 'geoCountry'
    | 'geoRegion'
    | 'interests'
    | 'jobs'
    | 'universities'
    | 'languages'
    | 'talentBaseUserProfile'
>

/** Owner-only reusable file. Applications upload their own independent copy. */
export interface SavedJobCv {
    path: string
    name: string
    size: number
    savedAt: string
}

export type UserPrivateProfile = DocumentWithTimestamps & {
    id: UserId
    contactEmail?: string
    phone?: string
    birthday?: string
    consents?: ConsentsData
    rulesAccepted?: Timestamp
    roles: UserRoles[]
    favorites?: UserFavorites
    lastLoggedIn?: Timestamp
    requestLimits?: {
        volunteering?: RequestLimit
        mentoring?: {
            daily: RequestLimit
            weekly: RequestLimit
        }
    }
    myProjects?: MyProjects
    jobFormDrafts?: JobApplicationFormDraft[]
    savedJobCv?: SavedJobCv | null
    updatedById?: string
    anonymizedAt?: Timestamp
    anonymizedById?: UserId
}

export interface FullUserProfile extends UserProfile, UserPrivateProfile {}

export interface DraftData {
    phone: string
    email: string
    about: string
    motivation: string
    linkedInUrl: string
}

export type JobApplicationFormDraft = DocumentWithTimestamps & {
    jobId: string
    orgId: string
    data: DraftData
}

export const defaultUserProfile = {
    firstName: '',
    lastName: '',
    profileImg: null,
    highSchools: [],
    universities: [],
    jobs: [],
    socialLinks: [],
    achievements: [],
    genders: [''],
    geoCountry: '',
    geoRegion: '',
    geoCity: '',
    shortProfile: '',
    interests: [],
    hobby: '',
    leafAlumni: false,
    leafAlumniWhatAnWhen: '',
    createdAt: new Date(),
    updatedAt: new Date()
} as any as UserProfile

export const defaultUserPrivateProfile = {
    contactEmail: '',
    phone: '',
    birthday: '',
    createdAt: new Date(),
    updatedAt: new Date()
} as UserPrivateProfile
